// Cleans HTML on the way in from the clipboard, before Tiptap parses it against the schema.
//
// The schema already drops tags it does not know, which is why pasting has never produced
// anything unsafe. What it does not do is stop Word's baggage from surviving as *valid*
// markup: a full stylesheet, an XML island, empty namespace paragraphs that become blank
// lines, and mso- style declarations that the font-size and colour extensions happily read
// as if the teacher had chosen them. That is what this removes.

// Only declarations one of our own extensions actually understands survive. An allow-list
// rather than a block-list, because Word invents properties faster than we could exclude
// them, and anything we do not recognise is by definition not formatting we can honour.
const KEPT_STYLE_PROPERTIES = new Set([
  "color",
  "font-size",
  "text-align",
  "width",
  "height",
]);

function keepStyle(declaration: string): boolean {
  const property = declaration.split(":")[0]?.trim().toLowerCase();
  return !!property && KEPT_STYLE_PROPERTIES.has(property);
}

export function cleanPastedHtml(html: string): string {
  if (!html) return html;

  const stripped = html
    // Word wraps alternate markup in downlevel conditional comments. The content inside is a
    // duplicate of what follows, so keeping it doubles the paste.
    .replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, "")
    .replace(/<!--\s*(?:Start|End)Fragment\s*-->/gi, "")
    // o:p is Word's paragraph mark. It is removed with its content, which is only ever a
    // non-breaking space; stripping just the tags would leave that space behind as real
    // text in the question.
    .replace(/<o:p[^>]*\/>/gi, "")
    .replace(/<o:p[^>]*>[\s\S]*?<\/o:p>/gi, "")
    // Remaining Office namespace elements: w:sdt, m:oMath, v:shape, st1:place. Tags only,
    // since these can wrap text worth keeping.
    .replace(/<\/?(?:o|w|m|v|x|st1):[^>]*>/gi, "")
    // Word and Excel ship a stylesheet and an XML island alongside the fragment.
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<xml[\s\S]*?<\/xml>/gi, "");

  const parsed = new DOMParser().parseFromString(stripped, "text/html");

  // A formula on its own line, copied from the math document tool: a centred paragraph here,
  // holding the same <math-field>, which the math extension takes as a formula.
  parsed.body.querySelectorAll("div[data-math-display]").forEach((div) => {
    const paragraph = parsed.createElement("p");
    paragraph.setAttribute("style", "text-align: center");
    paragraph.append(...Array.from(div.childNodes));
    div.replaceWith(paragraph);
  });

  parsed.body.querySelectorAll<HTMLElement>("*").forEach((element) => {
    const tag = element.tagName.toLowerCase();

    // Class survives only on code, where it carries the highlight language that
    // code-block-lowlight parses. Everywhere else it is Word's MsoNormal and friends,
    // which mean nothing here.
    // A diagram block pasted from a tool (the SQL tool's) is found by its class and its source.
    const diagram = tag === "div" && element.classList.contains("mermaid-diagram-block") && element.hasAttribute("data-mermaid-source");
    if (tag !== "code" && tag !== "pre" && !diagram) element.removeAttribute("class");
    element.removeAttribute("lang");

    const style = element.getAttribute("style");
    if (!style) return;
    const kept = style.split(";").map((d) => d.trim()).filter((d) => d && keepStyle(d));
    if (kept.length) element.setAttribute("style", kept.join("; "));
    else element.removeAttribute("style");
  });

  return parsed.body.innerHTML;
}
